import {
  ExcludedResponseTypeDTO,
  RespondClient,
  ResponseDTO,
  ResponseLibDTO,
  ResponseScenariosDTO,
  ResponseTypeDTO,
  ResponseTypeOrderRequest,
  ResponseVisibilityRequest,
  SaveResponse,
  SaveResponseLib,
  SaveResponseLibItem,
  SaveResponseTypeRequest,
  SearchAllResponseContentRequest,
  SearchResponseContentByLibraryRequest,
  SearchResponseContentByPortalRequest,
  SearchResponseResultsDTO,
  SimpleResponseLibDTO,
} from "./generatedFetchClient";
import { defineStore, acceptHMRUpdate } from "pinia";

const respondClient = new RespondClient();

export enum RespondSearchType {
  SearchAll,
  SearchLibrary,
  SearchPortal,
}

// A store instance to manage user and login state
export const useRespondStore = defineStore({
  id: "respondStore",
  state: () => {
    return {
      libraryList: [] as SimpleResponseLibDTO[],
      selectedPortalId: 0,
      selectedLibrary: null as ResponseLibDTO | null,
      selectedResponses: [] as ResponseDTO[],
      selectedExcludedResponseTypes: [] as ExcludedResponseTypeDTO[],
      responseTypes: [] as ResponseTypeDTO[],

      searchResults: {} as SearchResponseResultsDTO,
      searchType: null as RespondSearchType | null,
      searchText: "",
      searchLibraryId: null as number | null,
      searchPortalId: null as number | null,
      includeOverrides: false,
    };
  },
  getters: {},
  actions: {
    setAuthToken(token: string) {
      respondClient.setAuthToken(token);
    },
    async getLibraryList(force: boolean = false): Promise<string> {
      if (this.libraryList !== null && this.libraryList.length > 0 && !force) {
        return "";
      }

      try {
        this.libraryList = await respondClient.getLibraryList();
        return "";
      } catch (apiError: any) {
        console.error("Error getting responseLib list ", apiError);
        return apiError.response;
      }
    },
    async getLibrary(responseLibId: number, force: boolean = false): Promise<ResponseLibDTO> {
      if (
        this.selectedLibrary !== null &&
        this.selectedLibrary.responseLibId == responseLibId &&
        !force
      ) {
        return this.selectedLibrary;
      }

      try {
        this.selectedLibrary = await respondClient.getLibrary(responseLibId);
        return this.selectedLibrary;
      } catch (apiError: any) {
        console.error("Error getting responseLib ", apiError);
        return apiError.response;
      }
    },
    async getResponseTypes(force: boolean = false): Promise<ResponseTypeDTO[] | undefined> {
      if (this.responseTypes?.length > 0 && !force) {
        return this.responseTypes;
      }

      try {
        this.responseTypes = await respondClient.getResponseTypes();
      } catch (apiError: any) {
        console.error("Error getting response types", apiError);
      }
    },
    async getResponsesByPortal(
      portalId: number,
      includeHidden: boolean = false
    ): Promise<ResponseDTO[] | undefined> {
      try {
        this.selectedResponses = await respondClient.getResponsesByPortal(portalId, includeHidden);
        this.selectedPortalId = portalId;
        return this.selectedResponses;
      } catch (apiError: any) {
        console.error(`Error getting responses for portal ${portalId}`, apiError);
      }
    },
    async getExcludedResponseTypes(portalId: number): Promise<ExcludedResponseTypeDTO[]> {
      try {
        this.selectedExcludedResponseTypes = await respondClient.getExcludedResponseTypes(portalId);
        return this.selectedExcludedResponseTypes;
      } catch (apiError: any) {
        console.error(`Error getting excluded responses for portal ${portalId}`, apiError);
      }
      return [];
    },
    async saveResponseVisibility(request: ResponseVisibilityRequest): Promise<string> {
      try {
        await respondClient.saveResponseVisibility(request);
        return "";
      } catch (apiError: any) {
        let err = "Error saving response visibility " + apiError;
        console.error(err);
        return err;
      }
    },
    async SaveResponse(portalId: number, response: SaveResponse): Promise<string | undefined> {
      try {
        let pk = await respondClient.saveResponse(portalId, response);
        if (pk == 0) {
          return "No response record found to update";
        }
      } catch (apiError: any) {
        console.error(`Error saving response`, apiError);
        return apiError.response;
      }
    },
    async SaveResponseLib(responseLib: SaveResponseLib): Promise<string | undefined> {
      try {
        let pk = await respondClient.saveResponseLibrary(responseLib);
        if (pk == 0) {
          return "No response library record found to update";
        }
        if (this.selectedPortalId > 0) {
          await this.getResponsesByPortal(this.selectedPortalId, true);
        }
      } catch (apiError: any) {
        console.error(`Error saving response library`, apiError);
        return apiError.response;
      }
    },
    async SaveResponseLibItem(responseLibItem: SaveResponseLibItem): Promise<string | undefined> {
      try {
        if (
          (responseLibItem?.responseLibItemId || 0) <= 0 &&
          this.selectedLibrary?.responseLibItems?.find(
            (r) =>
              r.responseTypeId == responseLibItem.responseTypeId &&
              r.roleTypeId == responseLibItem.roleTypeId
          )
        )
          return "Role / Scenario combination already exists.";
        let pk = await respondClient.saveResponseLibItem(responseLibItem);
        if (pk == 0) {
          return "No response library record found to update";
        }
        await this.getResponsesByPortal(this.selectedPortalId, true);
      } catch (apiError: any) {
        console.error(`Error saving response library`, apiError);
        return apiError.response;
      }
    },
    async saveResponseType(responseType: SaveResponseTypeRequest): Promise<string> {
      try {
        await respondClient.saveResponseType(responseType);
        return "";
      } catch (apiError: any) {
        let err = "Error saving response type " + apiError;
        console.error(err);
        return err;
      }
    },
    async deleteResponse(responseId: number): Promise<string> {
      try {
        await respondClient.deleteResponse(responseId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting Response", apiError);
        return apiError.response;
      }
    },
    async deleteResponseLibItem(responseLibItemId: number): Promise<string> {
      try {
        await respondClient.deleteResponseLibItem(responseLibItemId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting ResponseLibItem", apiError);
        return apiError.response;
      }
    },
    async deleteResponseType(responseTypeId: number): Promise<string> {
      try {
        await respondClient.deleteResponseType(responseTypeId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting ResponseType ", apiError);
        return apiError.response;
      }
    },
    async deleteResponseLibrary(responseLibId: number): Promise<string> {
      try {
        await respondClient.deleteResponseLibrary(responseLibId);
        return "";
      } catch (apiError: any) {
        console.error("Error deleting response library ", apiError);
        return apiError.response;
      }
    },
    async searchAllResponseContent(
      searchText: string,
      includeOverrides: boolean = false
    ): Promise<string> {
      searchText = searchText.toLocaleLowerCase();

      this.searchType = RespondSearchType.SearchAll;
      this.searchText = searchText;
      this.includeOverrides = includeOverrides;

      try {
        this.searchResults = await respondClient.searchAllResponseContent({
          searchText: searchText,
          includeOverrides: includeOverrides,
        } as SearchAllResponseContentRequest);
        return "";
      } catch (apiError: any) {
        console.error("Error searching all response content ", apiError);
        return apiError.response;
      }
    },
    async searchResponseContentByLibrary(
      searchText: string,
      responseLibId: number
    ): Promise<string> {
      searchText = searchText.toLocaleLowerCase();

      this.searchType = RespondSearchType.SearchLibrary;
      this.searchText = searchText;
      this.searchLibraryId = responseLibId;

      // Search text on client if library is already loaded
      if (responseLibId == this.selectedLibrary?.responseLibId) {
        let libItemResults = this.selectedLibrary?.responseLibItems?.filter((i) =>
          i?.responseText?.toLocaleLowerCase().includes(searchText)
        );

        this.searchResults.libItems = libItemResults;
        this.searchResults.overrides = [];

        return "";
      }

      try {
        this.searchResults = await respondClient.searchResponseContentByLibrary({
          responseLibId: responseLibId,
          searchText: searchText,
        } as SearchResponseContentByLibraryRequest);
        return "";
      } catch (apiError: any) {
        console.error("Error searching response library ", apiError);
        return apiError.response;
      }
    },
    async searchResponseContentByPortal(searchText: string, portalId: number): Promise<string> {
      searchText = searchText.toLocaleLowerCase();

      this.searchType = RespondSearchType.SearchPortal;
      this.searchText = searchText;
      this.searchPortalId = portalId;

      // TODO:
      // Add "include hidden" checkbox to respond search
      // If off, try searching in client if portalId matches
      // If on, or if portalId doesn't match, search with API
      // In api call, set isHidden = isHidden || *scenarioIsExcludedFromPortal*
      // Display isHidden somehow in results dump

      // OR
      // Scrap the current hidden functionality. I hate that there are two ways to hide things
      // Replace the "Choose Scenario Visibility" modal with a bulk edit that auto sets all response content in a scenario to isHidden or vice versa
      // Then, this method can work totally fine if the current portal's response content is already loaded
      // Also will help reason about stuff in the future with only the one isHidden attribute rather than three to deal with
      // Maybe can meet with Erik because it seemed he had some qualms / misgivings about the multiple ways to do it too

      try {
        this.searchResults = await respondClient.searchResponseContentByPortal({
          portalId: portalId,
          searchText: searchText,
        } as SearchResponseContentByPortalRequest);
        return "";
      } catch (apiError: any) {
        console.error("Error searching portal response content ", apiError);
        return apiError.response;
      }
    },
    async saveResponseOrder(newOrder: ResponseTypeOrderRequest) {
      try {
        await respondClient.saveResponsePortalOrder(newOrder);
      } catch (apiError: any) {
        console.error("Error saving response order", apiError);
        return apiError.response;
      }
    },
    async getResponseScenarios(responseLibId: number): Promise<ResponseScenariosDTO> {
      try {
        return await respondClient.getResponseScenarios(responseLibId);
      } catch (apiError: any) {
        console.error("Error getting response scenarios ", apiError);
        return apiError.response;
      }
    },
  },
});

// Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(
    acceptHMRUpdate(useRespondStore as any, (import.meta as any).hot)
  );
}
